import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Copy, Check, ShieldCheck, ShoppingBag, ChevronDown, Truck, BadgePercent, Sparkles, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import SeasonalDiscountRow from "../components/SeasonalDiscountRow";
import { useAuth } from "../context/AuthContext";
import ProductImage from "../components/ProductImage";
import { US_STATES } from "../data/usStates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZELLE_EMAIL = "luviarx@gmail.com";
const ZELLE_NAME = "LuviaRx LLC";
const DRAFT_KEY = "luviarx_active_order_v1"; // sessionStorage — avoids spawning a fresh order on every refresh

const FREE_STANDARD_THRESHOLD_FALLBACK = 300;
const STANDARD_PRICE_FALLBACK = 14;
const STANDARD_CARRIER = "usps_priority";

export default function ReviewOrder() {
  const {
    detailed, subtotal, clear,
    volumeDiscountPct, volumeDiscountAmount,
    promoCode, promoCodes, appliedCoupons,
    promoCodePct, promoCodeAmount, promoCodeAmountOff, promoCodeType,
    addPromoCode, removePromoCode, clearPromoCode,
    personalDiscountPct, personalDiscountAmount, personalDiscountLabel,
    totalDiscountAmount, discountedTotal, seasonalPromo,
  } = useCart();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [orderNumberCopied, setOrderNumberCopied] = useState(false);

  // Payment method — customer picks between Stripe (card + ACH bank) or Zelle.
  // Stripe is only offered if backend has STRIPE_SECRET_KEY configured.
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" | "zelle"
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/config`);
        if (cancelled) return;
        setStripeEnabled(!!data?.stripe_enabled);
        if (!data?.stripe_enabled) setPaymentMethod("zelle");
      } catch {
        if (!cancelled) {
          setStripeEnabled(false);
          setPaymentMethod("zelle");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Draft order number — generated on mount via POST /api/orders. Persisted in
  // sessionStorage so refreshes don't keep creating new pending orders.
  const [orderNumber, setOrderNumber] = useState(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.order_number || "";
    } catch { return ""; }
  });
  const [draftError, setDraftError] = useState("");
  const draftPostedRef = useRef(false);
  // Signature of the items+promo_codes that were last posted (or synced) to
  // the server. If the user modifies their cart, this diverges from the
  // current signature and we PUT /orders/{n}/items to resync — otherwise
  // the paid order would ship with the stale, first-mount cart contents.
  const lastSyncedSigRef = useRef((() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.sig || "";
    } catch { return ""; }
  })());

  // Shipping form state — pre-fills the legal name from the user profile,
  // everything else starts empty.
  const [shipping, setShipping] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
  });
  const setField = (key) => (e) =>
    setShipping((prev) => ({ ...prev, [key]: e.target.value }));

  // Shipping selection (standard only customer-facing; admin label-buy can
  // still pick any carrier). The live `standardPrice` + `freeThreshold` flow
  // from `/api/shipping/rates` so admin Mongo changes apply without redeploy.
  const shippingMethod = "standard";
  const shippingCarrier = STANDARD_CARRIER;
  const [shippingOptions, setShippingOptions] = useState(null);
  const [shipmentId, setShipmentId] = useState(null);
  const [rateIds, setRateIds] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [freeThreshold, setFreeThreshold] = useState(FREE_STANDARD_THRESHOLD_FALLBACK);
  const [standardPrice, setStandardPrice] = useState(STANDARD_PRICE_FALLBACK);
  // Admin-controlled global "Free Shipping" flag — when on, all orders ship
  // for $0 regardless of subtotal and the threshold banner is hidden.
  const [freeShippingEnabled, setFreeShippingEnabled] = useState(false);

  // Validate any cached promo codes on mount so codes that are no longer
  // eligible for THIS user (e.g. FIRST10 already used on a prior order) get
  // stripped from state + localStorage instead of quietly persisting and
  // giving the customer a discount they haven't earned. Only runs once per
  // token change; the check is silent when the code is still valid.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      for (const p of promoCodes) {
        try {
          const { data } = await axios.post(
            `${API}/discounts/validate`,
            { code: p.code },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (cancelled) return;
          if (!data?.valid) removePromoCode(p.code);
        } catch { /* leave in place — server error, retry on submit */ }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Prime the shipping price + threshold from the live admin settings on
  // mount, so the review page shows the correct amount BEFORE the customer
  // fills in a full address (POST /shipping/rates requires the full address
  // because it also calls EasyPost). Any admin edit takes effect on the
  // next page load — no redeploy required.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/shipping/public/settings`)
      .then(({ data }) => {
        if (cancelled) return;
        if (typeof data?.standard_price === "number") setStandardPrice(data.standard_price);
        if (typeof data?.free_threshold === "number") setFreeThreshold(data.free_threshold);
        if (typeof data?.free_shipping_enabled === "boolean") {
          setFreeShippingEnabled(data.free_shipping_enabled);
        }
      })
      .catch(() => { /* fall back to static defaults */ });
    return () => { cancelled = true; };
  }, []);

  const isStandardFree = freeShippingEnabled || discountedTotal >= freeThreshold;
  const shippingCost = isStandardFree ? 0 : standardPrice;
  // Total the customer would pay via Stripe (no bank-transfer discount).
  const totalBeforeZelleDiscount = useMemo(
    () => +(discountedTotal + shippingCost).toFixed(2),
    [discountedTotal, shippingCost],
  );
  // Zelle bank transfer saves the customer 3% (we skip the Stripe fee).
  // Applied to the merchandise subtotal only — not shipping.
  const ZELLE_DISCOUNT_PCT = 3;
  const zelleDiscountAmount = useMemo(
    () => +(discountedTotal * (ZELLE_DISCOUNT_PCT / 100)).toFixed(2),
    [discountedTotal],
  );
  const zelleTotal = useMemo(
    () => +(totalBeforeZelleDiscount - zelleDiscountAmount).toFixed(2),
    [totalBeforeZelleDiscount, zelleDiscountAmount],
  );
  // finalTotal is what the customer actually pays given their payment choice.
  const finalTotal = paymentMethod === "zelle" ? zelleTotal : totalBeforeZelleDiscount;

  const hasUnavailable = detailed.some(
    (d) => !d.variant.inStock || d.variant.stock <= 0 || d.qty > d.variant.stock
  );

  // ---------- Create OR sync draft order on cart change ----------------
  //
  // On first mount: POST /api/orders → new draft.
  // On subsequent mounts (customer went back to cart and returned): if the
  // cart signature has diverged from the last-posted signature, PUT
  // /api/orders/{n}/items to sync the draft. Without this, the customer
  // pays for whatever was in the cart at the FIRST /checkout/review visit,
  // not their final cart contents.
  useEffect(() => {
    if (!token || detailed.length === 0 || hasUnavailable) return;

    // Signature = sorted list of (slug, variantId, qty) + sorted promo codes.
    // Stable string so we can compare cheaply.
    const sig = JSON.stringify({
      i: detailed
        .map((d) => `${d.slug}|${d.variant.id}|${d.qty}`)
        .sort(),
      p: promoCodes.map((p) => p.code).slice().sort(),
    });

    const itemsPayload = {
      items: detailed.map((d) => ({
        product_id: d.slug,
        variant_id: d.variant.id,
        quantity: d.qty,
      })),
      promo_codes: promoCodes.map((p) => p.code),
    };

    // -- Path 1: existing draft, cart unchanged since last sync → no-op.
    if (orderNumber && sig === lastSyncedSigRef.current) return;

    // -- Path 2: existing draft, cart changed → PUT sync (keep same order#).
    if (orderNumber && sig !== lastSyncedSigRef.current) {
      axios
        .put(`${API}/orders/${orderNumber}/items`, itemsPayload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          lastSyncedSigRef.current = sig;
          sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
            order_number: orderNumber, sig, ts: Date.now(),
          }));
        })
        .catch((e) => {
          // 409 = draft already advanced past pending; a fresh order will
          // be created on the next mount if the customer goes back again.
          const status = e?.response?.status;
          if (status === 409) return;
          setDraftError(
            e?.response?.data?.detail
              || "Could not update your order. Please refresh and try again.",
          );
        });
      return;
    }

    // -- Path 3: no draft yet → POST create.
    if (draftPostedRef.current) return; // StrictMode double-mount protection
    draftPostedRef.current = true;
    axios
      .post(`${API}/orders`, itemsPayload, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setOrderNumber(data.order_number);
        lastSyncedSigRef.current = sig;
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
          order_number: data.order_number, sig, ts: Date.now(),
        }));
      })
      .catch((e) => {
        draftPostedRef.current = false;
        setDraftError(
          e?.response?.data?.detail ||
          "Could not create your order. Please refresh and try again.",
        );
      });
  }, [token, detailed, hasUnavailable, orderNumber, promoCodes]);
  // ---------------------------------------------------------------------

  // ---------- Fetch shipping rates once the address is filled out ------
  // NOTE: phone must contain at least 7 digits before we fire EasyPost —
  // otherwise the rates call would be made repeatedly during phone entry
  // (e.g. every keystroke of "5", "55", "555"…), each returning a 422 that
  // shows a scary "we couldn't verify your address" toast to the customer.
  // Customer reports of "kicked out when I typed my phone" traced back to
  // exactly this: the intermediate error appeared while the phone was
  // half-typed and made the checkout look broken.
  const isShippingComplete = !!(
    shipping.first_name.trim() && shipping.last_name.trim() &&
    shipping.address1.trim() && shipping.city.trim() &&
    shipping.state && shipping.zip_code.trim() &&
    shipping.phone.replace(/\D/g, "").length >= 7
  );

  useEffect(() => {
    if (!token || !isShippingComplete) {
      setShippingOptions(null);
      return;
    }
    setRatesLoading(true);
    setRatesError("");
    const handle = setTimeout(() => {
      axios
        .post(
          `${API}/shipping/rates`,
          {
            shipping: { ...shipping, address2: shipping.address2 || undefined },
            subtotal: discountedTotal,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then(({ data }) => {
          setShippingOptions(data.options || null);
          setShipmentId(data.shipment_id || null);
          setRateIds(data.rate_ids || {});
          if (typeof data.free_standard_threshold === "number") {
            setFreeThreshold(data.free_standard_threshold);
          }
          if (typeof data.free_shipping_enabled === "boolean") {
            setFreeShippingEnabled(data.free_shipping_enabled);
          }
          // The standard tier's price field IS the live admin-configured
          // price (with the free-threshold rule already applied for the
          // current subtotal). We need the *base* price, not the
          // already-zeroed-out price, so derive it instead:
          // if free is unlocked, the API returns price 0 — keep the
          // previous standardPrice; otherwise sync to the returned price.
          const standardTier = (data.options || []).find((o) => o.method === "standard");
          if (standardTier && standardTier.price > 0) {
            setStandardPrice(standardTier.price);
          }
        })
        .catch((e) => {
          // Soft-fail — we still let the customer pick from the static options
          setShippingOptions(null);
          setShipmentId(null);
          setRateIds({});
          setRatesError(
            e?.response?.data?.detail ||
            "We couldn't verify your address with our carrier. You can still choose a shipping method.",
          );
        })
        .finally(() => setRatesLoading(false));
    }, 600);  // small debounce
    return () => clearTimeout(handle);
  }, [token, isShippingComplete, shipping.address1, shipping.address2,
      shipping.city, shipping.state, shipping.zip_code, shipping.phone,
      discountedTotal]);
  // ---------------------------------------------------------------------

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked in some contexts; silent */ }
  };

  const copyOrderNumber = async () => {
    if (!orderNumber) return;
    try {
      await navigator.clipboard.writeText(orderNumber);
      setOrderNumberCopied(true);
      setTimeout(() => setOrderNumberCopied(false), 1500);
    } catch { /* clipboard blocked; silent */ }
  };

  const validateShipping = () => {
    const required = ["first_name", "last_name", "address1", "city", "state", "zip_code", "phone"];
    for (const k of required) {
      if (!String(shipping[k] || "").trim()) {
        return `Please fill in your ${k.replace("_", " ")}.`;
      }
    }
    if (!/^[A-Z0-9 -]{3,12}$/i.test(shipping.zip_code.trim())) {
      return "Please enter a valid ZIP code.";
    }
    return "";
  };

  const submitOrder = async () => {
    setError("");
    if (detailed.length === 0) {
      setError("Your cart is empty.");
      return;
    }
    if (hasUnavailable) {
      setError("One or more items in your cart are unavailable. Return to your cart to resolve.");
      return;
    }
    if (!orderNumber) {
      setError("Your order number is still being generated. Please wait a moment.");
      return;
    }
    const shippingErr = validateShipping();
    if (shippingErr) {
      setError(shippingErr);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        shipping: {
          ...shipping,
          address2: shipping.address2.trim() || undefined,
        },
        shipping_method: shippingMethod,
        shipping_carrier: shippingCarrier,
        easypost_shipment_id: shipmentId,
        easypost_rate_id: rateIds[shippingCarrier] || null,
        payment_method: paymentMethod,
      };
      const { data } = await axios.post(
        `${API}/orders/${orderNumber}/acknowledge`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (paymentMethod === "stripe") {
        // Create the Stripe Checkout Session and redirect the browser.
        // We DON'T clear the cart here — if Stripe redirects back to /cancel,
        // the user can retry without losing their cart.
        const { data: co } = await axios.post(
          `${API}/payments/checkout`,
          {
            order_number: data.order_number,
            origin_url: window.location.origin,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (co?.checkout_url) {
          sessionStorage.removeItem(DRAFT_KEY);
          clear();
          window.location.assign(co.checkout_url);
          return;
        }
        setError("Could not start Stripe checkout. Please try again.");
        setSubmitting(false);
        return;
      }

      // Zelle path — same as before.
      clear();
      sessionStorage.removeItem(DRAFT_KEY);
      navigate(`/order/${data.order_number}`, { replace: true });
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string"
        ? raw
        : Array.isArray(raw)
        ? raw.map((r) => r?.msg || r?.type || "").filter(Boolean).join("; ")
        : "Could not place order. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  };

  if (detailed.length === 0) {
    return (
      <main data-testid="review-page-empty" className="py-20 md:py-28">
        <div className="container-x text-center max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-7 h-7 text-sage-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-4xl font-bold text-charcoal-600 mb-3">Your cart is empty</h1>
          <p className="text-charcoal-400 mb-8">Browse our research compound catalog to get started.</p>
          <Link to="/products" className="btn-primary">
            Browse Compounds <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="review-page" className="py-12 md:py-16 bg-cream-200/30">
      <div className="container-x">
        <Link to="/cart" className="inline-flex items-center text-sm text-charcoal-400 hover:text-charcoal-600 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to cart
        </Link>
        <p className="overline mb-2">Order Review</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 mb-6">
          Confirm & pay
        </h1>

        {/* Prominent order number banner — generated immediately on landing
            so the customer has it BEFORE they send their Zelle transfer. */}
        <section
          data-testid="order-number-banner"
          className="surface-card p-6 md:p-7 mb-8 border-2 border-[#8B2252]/15"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1.5">
                Your order number
              </p>
              {orderNumber ? (
                <p
                  data-testid="review-order-number"
                  className="font-display font-bold text-3xl md:text-4xl text-[#8B2252] tracking-wide select-all"
                >
                  {orderNumber}
                </p>
              ) : draftError ? (
                <p data-testid="review-order-number-error" className="text-sm text-red-700">
                  {draftError}
                </p>
              ) : (
                <p className="font-display text-2xl text-charcoal-300">Generating…</p>
              )}
              <p className="text-xs text-charcoal-400 mt-2 max-w-md">
                {paymentMethod === "stripe"
                  ? "We'll attach this reference to your Stripe payment so we can match it to your shipment."
                  : "Use this as the memo on your Zelle transfer so we can match your payment."}
              </p>
            </div>
            <button
              type="button"
              onClick={copyOrderNumber}
              disabled={!orderNumber}
              data-testid="copy-order-number-button"
              className="btn-secondary self-stretch md:self-auto disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Copy order number"
            >
              {orderNumberCopied
                ? (<><Check className="w-4 h-4 mr-2" strokeWidth={2} /> Copied</>)
                : (<><Copy className="w-4 h-4 mr-2" strokeWidth={2} /> Copy Order Number</>)}
            </button>
          </div>
        </section>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* LEFT: Items + payment instructions */}
          <div className="lg:col-span-7 space-y-8">
            <section data-testid="review-items" className="surface-card p-6 md:p-7">
              <p className="overline mb-4">Items</p>
              <div className="divide-y divide-charcoal-100/60">
                {detailed.map((item) => {
                  const lineKey = `${item.slug}-${item.variant.id}`;
                  return (
                    <div
                      key={lineKey}
                      data-testid={`review-row-${lineKey}`}
                      className="py-4 flex gap-4 items-center"
                    >
                      <ProductImage
                        src={item.image}
                        fallback={item.imageFallback}
                        alt={item.name}
                        className="w-16 h-20 object-cover rounded-xl"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-charcoal-600">{item.name}</p>
                        <p className="text-xs text-charcoal-400 mt-0.5">{item.variant.label}</p>
                        <p className="text-xs text-charcoal-400 mt-1">Quantity: {item.qty}</p>
                      </div>
                      <span className="font-display font-bold text-charcoal-600">
                        ${item.lineTotal.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section
              data-testid="shipping-form"
              className="surface-card p-6 md:p-7"
            >
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-sage-600" strokeWidth={1.8} />
                <p className="overline">Shipping Address</p>
              </div>
              <p className="text-xs text-charcoal-400 mb-5">
                All fields required unless marked optional. We ship via USPS / UPS Ground.
              </p>

              <SavedAddressPicker
                token={token}
                onSelect={(addr) => {
                  setShipping({
                    first_name: addr.first_name || "",
                    last_name:  addr.last_name  || "",
                    address1:   addr.address1   || "",
                    address2:   addr.address2   || "",
                    city:       addr.city       || "",
                    state:      addr.state      || "",
                    zip_code:   addr.zip_code   || "",
                    phone:      addr.phone      || "",
                  });
                }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ShipField label="First name" htmlFor="ship-first">
                  <input
                    id="ship-first"
                    data-testid="ship-first-name"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={shipping.first_name}
                    onChange={setField("first_name")}
                    className="input"
                  />
                </ShipField>
                <ShipField label="Last name" htmlFor="ship-last">
                  <input
                    id="ship-last"
                    data-testid="ship-last-name"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={shipping.last_name}
                    onChange={setField("last_name")}
                    className="input"
                  />
                </ShipField>
                <div className="sm:col-span-2">
                  <ShipField label="Address line 1" htmlFor="ship-addr1">
                    <input
                      id="ship-addr1"
                      data-testid="ship-address1"
                      type="text"
                      autoComplete="address-line1"
                      required
                      value={shipping.address1}
                      onChange={setField("address1")}
                      className="input"
                      placeholder="Street, building, etc."
                    />
                  </ShipField>
                </div>
                <div className="sm:col-span-2">
                  <ShipField label="Address line 2 (optional)" htmlFor="ship-addr2">
                    <input
                      id="ship-addr2"
                      data-testid="ship-address2"
                      type="text"
                      autoComplete="address-line2"
                      value={shipping.address2}
                      onChange={setField("address2")}
                      className="input"
                      placeholder="Apt, suite, lab number…"
                    />
                  </ShipField>
                </div>
                <ShipField label="City" htmlFor="ship-city">
                  <input
                    id="ship-city"
                    data-testid="ship-city"
                    type="text"
                    autoComplete="address-level2"
                    required
                    value={shipping.city}
                    onChange={setField("city")}
                    className="input"
                  />
                </ShipField>
                <ShipField label="State" htmlFor="ship-state">
                  <div className="relative">
                    <select
                      id="ship-state"
                      data-testid="ship-state"
                      required
                      value={shipping.state}
                      onChange={setField("state")}
                      className="input appearance-none pr-10"
                    >
                      <option value="" disabled>Select state…</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400" />
                  </div>
                </ShipField>
                <ShipField label="ZIP code" htmlFor="ship-zip">
                  <input
                    id="ship-zip"
                    data-testid="ship-zip"
                    type="text"
                    autoComplete="postal-code"
                    required
                    value={shipping.zip_code}
                    onChange={setField("zip_code")}
                    className="input"
                    placeholder="e.g. 02139"
                  />
                </ShipField>
                <ShipField label="Phone number" htmlFor="ship-phone">
                  <input
                    id="ship-phone"
                    data-testid="ship-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    value={shipping.phone}
                    onChange={setField("phone")}
                    onKeyDown={(e) => {
                      // Prevent Enter from bubbling to any wrapping form —
                      // some mobile browsers (Safari iOS) treat Enter in a
                      // type=tel field as a form submit which was navigating
                      // customers off the checkout page.
                      if (e.key === "Enter") e.preventDefault();
                    }}
                    className="input"
                    placeholder="+1 555 555 0123"
                  />
                  <p
                    data-testid="ship-phone-hint"
                    className="mt-1.5 text-[11px] text-charcoal-400"
                  >
                    Any format accepted (e.g. 555-555-5555)
                  </p>
                </ShipField>
              </div>
            </section>

            {/* Shipping method preview — appears once the address is filled out. */}
            <ShippingMethodSelector
              isAddressComplete={isShippingComplete}
              loading={ratesLoading}
              error={ratesError}
              options={shippingOptions}
              isStandardFree={isStandardFree}
              freeThreshold={freeThreshold}
            />

            {/* Payment method selector — Zelle is the "smart choice" (3% off,
                no processor fee); Stripe (card + ACH) is offered as the
                convenience option. */}
            <PaymentMethodSelector
              stripeEnabled={stripeEnabled}
              value={paymentMethod}
              onChange={setPaymentMethod}
              zelleDiscountPct={ZELLE_DISCOUNT_PCT}
              zelleDiscountAmount={zelleDiscountAmount}
              zelleTotal={zelleTotal}
              totalBeforeZelleDiscount={totalBeforeZelleDiscount}
            />

            {paymentMethod === "zelle" && (
            <section
              data-testid="zelle-instructions"
              className="surface-card p-6 md:p-7 border-2 border-[#8B2252]/15"
            >
              <p className="overline mb-3">Payment Instructions</p>
              <p className="text-charcoal-500 leading-relaxed">
                Send your exact total to{" "}
                <span className="font-display font-bold text-charcoal-600">{ZELLE_EMAIL}</span>{" "}
                ({ZELLE_NAME}) via <span className="font-display font-bold text-[#8B2252]">Zelle</span>.
                Use your order number{" "}
                <span
                  data-testid="zelle-inline-order-number"
                  className="font-display font-bold text-[#8B2252] select-all"
                >
                  {orderNumber || "—"}
                </span>{" "}
                as the memo so we can match your payment.
              </p>

              <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3 items-start">
                <div className="bg-cream-200/70 rounded-2xl px-4 py-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1">
                    Send Zelle to
                  </p>
                  <p data-testid="zelle-email" className="font-display font-bold text-charcoal-600 break-all">
                    {ZELLE_EMAIL}
                  </p>
                  <p className="text-xs text-charcoal-400 mt-0.5">{ZELLE_NAME}</p>
                </div>
                <button
                  type="button"
                  onClick={copyEmail}
                  data-testid="copy-zelle-email"
                  className="btn-secondary self-stretch sm:self-start"
                  aria-label="Copy Zelle email"
                >
                  {copied ? (
                    <><Check className="w-4 h-4 mr-2" strokeWidth={2} /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" strokeWidth={2} /> Copy email</>
                  )}
                </button>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-5 items-start">
                <img
                  src="/zelle-qr.png"
                  alt="Scan to pay LuviaRx via Zelle"
                  data-testid="zelle-qr-image"
                  className="w-44 h-44 sm:w-48 sm:h-48 rounded-2xl bg-white border border-charcoal-100 p-3 shrink-0"
                />
                <div className="text-sm text-charcoal-500 leading-relaxed">
                  <p className="font-medium text-charcoal-600 mb-1">Scan to pay from your phone</p>
                  <p>Open your bank app&apos;s Zelle screen, scan this QR, then enter the total below. Make sure your order number is in the memo.</p>
                </div>
              </div>
            </section>
            )}

            {paymentMethod === "stripe" && (
              <section
                data-testid="stripe-instructions"
                className="surface-card p-6 md:p-7 border-2 border-[#8B2252]/15"
              >
                <p className="overline mb-3">Payment</p>
                <p className="text-charcoal-500 leading-relaxed">
                  You&apos;ll be redirected to <span className="font-display font-bold text-[#8B2252]">Stripe</span>&apos;s
                  secure checkout page to complete your payment with a
                  <span className="font-display font-bold text-charcoal-600"> credit/debit card</span> or
                  <span className="font-display font-bold text-charcoal-600"> ACH bank transfer</span>.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-charcoal-500">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-sage-700 shrink-0" strokeWidth={2.2} />
                    Cards clear instantly and your order ships next business day.
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-sage-700 shrink-0" strokeWidth={2.2} />
                    ACH bank transfers take 3-5 business days to clear; we&apos;ll email you at each step.
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-sage-700 shrink-0" strokeWidth={2.2} />
                    Your card and bank details go directly to Stripe — LuviaRx never sees them.
                  </li>
                </ul>
              </section>
            )}
          </div>

          {/* RIGHT: Summary + CTA */}
          <div className="lg:col-span-5">
            <div className="surface-card p-6 md:p-7 sticky top-28">
              <h2 className="font-display font-bold text-xl text-charcoal-600 mb-5">Order summary</h2>

              {seasonalPromo ? (
                <div
                  data-testid="review-seasonal-codes-notice"
                  className="mb-5 rounded-2xl p-4 border text-sm"
                  style={{ background: "#FDFAF9", borderColor: "rgba(139,34,82,0.18)", color: "#1C1618" }}
                >
                  <p className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#8B2252" }}>
                    {seasonalPromo.name} applied
                  </p>
                  <p style={{ color: "#9a7a80" }}>
                    {seasonalPromo.pct}% off is already applied and can’t be combined with other discounts
                    {seasonalPromo.allowFirst10 ? " — except FIRST10 on your first order, which you can still add below." : " or promo codes."}
                  </p>
                  {(promoCodes || []).filter((c) => !(seasonalPromo.allowFirst10 && c.code === "FIRST10")).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {promoCodes.filter((c) => !(seasonalPromo.allowFirst10 && c.code === "FIRST10")).map((c) => (
                        <span
                          key={c.code}
                          data-testid={`review-paused-code-${c.code}`}
                          className="inline-flex items-center gap-2 rounded-full bg-charcoal-100/60 px-3 py-1 text-xs text-charcoal-500 line-through decoration-charcoal-300"
                        >
                          {c.code}
                          <button
                            type="button"
                            onClick={() => removePromoCode(c.code)}
                            data-testid={`review-remove-paused-code-${c.code}`}
                            className="no-underline text-charcoal-400 hover:text-[#8B2252]"
                            aria-label={`Remove ${c.code}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
              {(!seasonalPromo || seasonalPromo.allowFirst10) && (
                <PromoCodeForm
                  token={token}
                  appliedCoupons={appliedCoupons}
                  onApply={addPromoCode}
                  onRemove={removePromoCode}
                />
              )}

              <div className="space-y-3 text-sm text-charcoal-500">
                <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                <SeasonalDiscountRow
                  promo={seasonalPromo}
                  testIdPrefix="review"
                  codesIgnored={(promoCodes || []).length > 0}
                />
                {volumeDiscountPct > 0 && (
                  <div data-testid="review-volume-discount" className="flex justify-between text-sage-700">
                    <span>Volume discount ({volumeDiscountPct}% off)</span>
                    <span className="font-medium">−${volumeDiscountAmount.toFixed(2)}</span>
                  </div>
                )}
                {appliedCoupons.length === 1 ? (
                  <div data-testid="review-promo-discount" className="flex justify-between text-[#b14a1f]">
                    <span>
                      {appliedCoupons[0].code} promo ({appliedCoupons[0].type === "amount"
                        ? `$${Number(appliedCoupons[0].amount_off).toFixed(2)} off`
                        : `${appliedCoupons[0].pct}% off`})
                    </span>
                    <span className="font-medium">
                      −${Number(appliedCoupons[0].discount_amount).toFixed(2)}
                    </span>
                  </div>
                ) : appliedCoupons.map((c) => (
                  <div
                    key={c.code}
                    data-testid={`review-promo-discount-${c.code}`}
                    className="flex justify-between text-[#b14a1f]"
                  >
                    <span>
                      {c.code} ({c.type === "amount"
                        ? `$${Number(c.amount_off).toFixed(2)} off`
                        : `${c.pct}% off`})
                    </span>
                    <span className="font-medium">
                      −${Number(c.discount_amount).toFixed(2)}
                    </span>
                  </div>
                ))}
                {personalDiscountPct > 0 && personalDiscountAmount > 0 && (
                  <div
                    data-testid="review-personal-discount"
                    className="flex justify-between font-medium"
                    style={{ color: "#8B2252" }}
                  >
                    <span>{personalDiscountLabel} discount ({personalDiscountPct}% off)</span>
                    <span>−${personalDiscountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div
                  data-testid="review-shipping-row"
                  className="flex justify-between"
                >
                  <span>
                    Shipping{" "}
                    <span className="text-charcoal-300 text-xs">
                      (Standard)
                    </span>
                  </span>
                  <span className="font-medium text-charcoal-600">
                    {shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                {!isStandardFree && (
                  <p data-testid="free-shipping-progress" className="text-[11px] text-charcoal-400 -mt-1">
                    Add ${(freeThreshold - discountedTotal).toFixed(2)} more to unlock
                    free Standard shipping (subtotal ${freeThreshold.toFixed(0)}+).
                  </p>
                )}
                {isStandardFree && !freeShippingEnabled && (
                  <p data-testid="free-shipping-unlocked" className="text-[11px] text-sage-700 -mt-1">
                    Free Standard shipping unlocked.
                  </p>
                )}
                {freeShippingEnabled && (
                  <p data-testid="free-shipping-flag-on" className="text-[11px] text-sage-700 -mt-1">
                    Free shipping on every order — no minimum.
                  </p>
                )}
                <div className="h-px bg-charcoal-100" />
                {paymentMethod === "zelle" && zelleDiscountAmount > 0 && (
                  <div
                    data-testid="review-zelle-discount"
                    className="flex justify-between text-sage-700 font-medium"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <BadgePercent className="w-3.5 h-3.5" strokeWidth={2} />
                      Zelle bank transfer ({ZELLE_DISCOUNT_PCT}% off)
                    </span>
                    <span>−${zelleDiscountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-1">
                  <span className="font-display font-bold text-charcoal-600">Total to send</span>
                  <span className="text-right">
                    {paymentMethod === "zelle" && zelleDiscountAmount > 0 && (
                      <span
                        data-testid="review-total-strikethrough"
                        className="block text-sm font-medium text-charcoal-300 line-through leading-none"
                      >
                        ${totalBeforeZelleDiscount.toFixed(2)}
                      </span>
                    )}
                    <span
                      data-testid="review-total"
                      className={`font-display font-bold text-2xl leading-tight ${
                        paymentMethod === "zelle" && zelleDiscountAmount > 0
                          ? "text-sage-700"
                          : "text-[#8B2252]"
                      }`}
                    >
                      ${finalTotal.toFixed(2)}
                    </span>
                  </span>
                </div>
                {totalDiscountAmount > 0 && (
                  <p className="text-[11px] text-charcoal-400">
                    You&apos;re saving ${totalDiscountAmount.toFixed(2)} on this order.
                  </p>
                )}
                {paymentMethod === "zelle" && zelleDiscountAmount > 0 && (
                  <p
                    data-testid="review-zelle-savings-line"
                    className="text-[11px] text-sage-700 font-medium"
                  >
                    Plus ${zelleDiscountAmount.toFixed(2)} saved by paying via Zelle.
                  </p>
                )}
              </div>

              {error && (
                <div data-testid="review-error" className="mt-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={submitOrder}
                disabled={submitting || hasUnavailable}
                data-testid="i-have-sent-payment-button"
                className="btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
                {submitting
                  ? (paymentMethod === "stripe" ? "Redirecting to Stripe…" : "Placing order…")
                  : (paymentMethod === "stripe" ? "Pay Securely — Card or Bank" : "I Have Sent My Payment")}
              </button>

              <p className="mt-5 text-[11px] text-charcoal-300 leading-relaxed">
                {paymentMethod === "stripe" ? (
                  <>You&apos;ll be redirected to Stripe&apos;s secure checkout. Your order ships once payment clears (instant for card, 3-5 business days for ACH bank transfer).</>
                ) : (
                  <>After clicking, you&apos;ll receive an order number. Your order is reserved and will ship once we confirm your Zelle transfer (usually within 1 business day).</>
                )}{" "}
                For Research Use Only. Not for Human Consumption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

const Row = ({ label, value, muted }) => (
  <div className="flex justify-between">
    <span className={muted ? "text-charcoal-300" : ""}>{label}</span>
    <span className={`font-medium ${muted ? "text-charcoal-300" : "text-charcoal-600"}`}>{value}</span>
  </div>
);

const ShipField = ({ label, htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
      {label}
    </span>
    {children}
  </label>
);

// Address book dropdown — pre-fills the shipping form on selection. Auto-
// loads the default address the first time it renders. Silent if the
// customer has no saved addresses.
const SavedAddressPicker = ({ token, onSelect }) => {
  const [addresses, setAddresses] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState("");
  const autoAppliedRef = React.useRef(false);

  React.useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios.get(`${API}/me/addresses`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setAddresses(data || []);
      })
      .catch(() => { if (!cancelled) setAddresses([]); });
    return () => { cancelled = true; };
  }, [token]);

  // First-load: auto-apply the default so returning customers don't retype.
  React.useEffect(() => {
    if (autoAppliedRef.current) return;
    if (!Array.isArray(addresses) || addresses.length === 0) return;
    const def = addresses.find((a) => a.is_default) || addresses[0];
    if (def) {
      setSelectedId(def.id);
      onSelect(def);
      autoAppliedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  if (!Array.isArray(addresses) || addresses.length === 0) return null;

  return (
    <div
      data-testid="review-saved-address-picker"
      className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2"
    >
      <label className="text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 sm:min-w-[110px]">
        Address book
      </label>
      <select
        data-testid="review-saved-address-select"
        value={selectedId}
        onChange={(e) => {
          const id = e.target.value;
          setSelectedId(id);
          if (!id) return;
          const addr = addresses.find((a) => a.id === id);
          if (addr) onSelect(addr);
        }}
        className="input text-sm max-w-full sm:max-w-md"
      >
        <option value="">— select a saved address —</option>
        {addresses.map((a) => (
          <option key={a.id} value={a.id}>
            {(a.label || `${a.first_name} ${a.last_name}`.trim() || a.address1)}
            {a.is_default ? " · default" : ""}
          </option>
        ))}
      </select>
    </div>
  );
};

// ---------- Payment method selector -------------------------------
// Zelle is presented as the "smart choice" (3% off, bank transfer, no
// processor fee). Stripe (card + ACH) is offered second as the
// convenience option. No option is labelled "Recommended" — the price
// difference speaks for itself.
const PaymentMethodSelector = ({
  stripeEnabled, value, onChange,
  zelleDiscountPct, zelleDiscountAmount, zelleTotal, totalBeforeZelleDiscount,
}) => (
  <section
    data-testid="payment-method-selector"
    className="surface-card p-6 md:p-7"
  >
    <p className="overline mb-3">Payment Method</p>
    <div className="grid gap-3">
      <PaymentMethodOption
        testId="payment-method-zelle"
        selected={value === "zelle"}
        onClick={() => onChange("zelle")}
        title="Zelle bank transfer"
        subtitle="Direct from your bank app · No card fees · Ships once we verify"
        icon={Sparkles}
        highlight
        badge={
          zelleDiscountPct > 0
            ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold px-2.5 py-1 rounded-full bg-sage-600 text-white">
                <BadgePercent className="w-3 h-3" strokeWidth={2.5} />
                Save {zelleDiscountPct}%
              </span>
            )
            : null
        }
        priceLine={
          zelleDiscountAmount > 0 && (
            <span
              data-testid="payment-method-zelle-price"
              className="mt-1 inline-flex items-baseline gap-2 text-xs"
            >
              <span className="text-sage-700 font-display font-bold text-base">
                ${zelleTotal.toFixed(2)}
              </span>
              <span className="text-charcoal-300 line-through">
                ${totalBeforeZelleDiscount.toFixed(2)}
              </span>
              <span className="text-sage-700 font-medium">
                save ${zelleDiscountAmount.toFixed(2)}
              </span>
            </span>
          )
        }
      />
      {stripeEnabled && (
        <PaymentMethodOption
          testId="payment-method-stripe"
          selected={value === "stripe"}
          onClick={() => onChange("stripe")}
          title="Card or Bank"
          subtitle="Powered by Stripe · Visa, Mastercard, Amex, Discover + ACH bank transfer"
          priceLine={
            <span
              data-testid="payment-method-stripe-price"
              className="mt-1 inline-block text-xs text-charcoal-500 font-medium"
            >
              ${totalBeforeZelleDiscount.toFixed(2)}
            </span>
          }
        />
      )}
    </div>
    {!stripeEnabled && (
      <p className="mt-3 text-[11px] text-charcoal-400">
        Card + ACH payments coming soon. For now, Zelle keeps things simple.
      </p>
    )}
  </section>
);

const PaymentMethodOption = ({
  testId, selected, onClick, title, subtitle, badge, priceLine,
  icon: Icon, highlight,
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-2xl border-2 p-4 md:p-5 transition-all duration-200 flex items-start gap-4 relative ${
      selected
        ? highlight
          ? "border-sage-600 bg-sage-50 shadow-[0_6px_18px_-8px_rgba(63,124,89,0.4)]"
          : "border-[#8B2252] bg-[#8B2252]/[0.04] shadow-[0_4px_12px_-6px_rgba(139,34,82,0.35)]"
        : highlight
          ? "border-sage-300 bg-sage-50/40 hover:border-sage-500 hover:bg-sage-50/70"
          : "border-charcoal-100 bg-cream-100 hover:border-charcoal-200 hover:bg-cream-200/40"
    }`}
  >
    <span
      className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
        selected
          ? highlight
            ? "border-sage-600 bg-sage-600"
            : "border-[#8B2252] bg-[#8B2252]"
          : "border-charcoal-200 bg-white"
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-white" />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-2 flex-wrap">
        {Icon && highlight && (
          <Icon
            className={`w-4 h-4 ${selected ? "text-sage-700" : "text-sage-600"}`}
            strokeWidth={2}
          />
        )}
        <span className="font-display font-bold text-charcoal-600">{title}</span>
        {badge}
      </span>
      <span className="block text-xs text-charcoal-400 mt-0.5 leading-relaxed">{subtitle}</span>
      {priceLine}
    </span>
  </button>
);

// ---------- Apply promo code form (FIRST10 + admin-managed codes) ----------
const PromoCodeForm = ({ token, appliedCoupons, onApply, onRemove }) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // "empty" = form is closed until user clicks "Add another code" (when at
  // least one coupon is already applied). "open" = form visible.
  const [entryOpen, setEntryOpen] = useState(false);
  const hasAny = (appliedCoupons || []).length > 0;

  // One-click welcome-offer toast. Instead of the old localStorage
  // auto-apply (which stacked FIRST10 across every future order), we now
  // ask the server whether THIS user is still eligible for FIRST10 and
  // show a subtle "Apply" button. Repeat customers see nothing here.
  const [welcome, setWelcome] = useState(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/discounts/welcome-offer`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => { if (!cancelled) setWelcome(data); })
      .catch(() => { /* silent — no toast if endpoint fails */ });
    return () => { cancelled = true; };
  }, [token]);
  const welcomeApplied = welcome
    && (appliedCoupons || []).some((c) => c.code === welcome.code);
  const showWelcome = welcome?.eligible && !welcomeApplied;

  const applyCode = async (code, closeAfter = true) => {
    setError(""); setSuccess("");
    if (!token) {
      setError("Please sign in to apply a promo code.");
      return false;
    }
    if ((appliedCoupons || []).some((c) => c.code === code)) {
      setError(`${code} is already applied.`);
      return false;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/discounts/validate`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.valid) {
        const amt = Number(data.amount_off) || 0;
        const pct = Number(data.percent) || 0;
        onApply(data.code, pct, amt);
        setSuccess(amt > 0
          ? `${data.code} applied — $${amt.toFixed(2)} off.`
          : `${data.code} applied — ${pct}% off.`);
        if (closeAfter) { setInput(""); setEntryOpen(false); }
        return true;
      }
      setError(data.reason || "Invalid code.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not apply promo code.");
    } finally {
      setSubmitting(false);
    }
    return false;
  };

  const apply = async (e) => {
    e?.preventDefault?.();
    const code = input.trim().toUpperCase();
    if (!code) {
      setError("Please enter a promo code.");
      return;
    }
    await applyCode(code, /* closeAfter */ true);
  };

  return (
    <div className="mb-5 space-y-3" data-testid="promo-code-block">
      {(appliedCoupons || []).map((c) => (
        <div
          key={c.code}
          data-testid={`applied-coupon-${c.code}`}
          className="rounded-2xl p-4 bg-sage-50 border border-sage-200 flex items-center justify-between gap-3"
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-0.5">
              Coupon applied
            </p>
            <p className="font-display font-bold text-charcoal-600">
              {c.code}{" "}
              <span className="text-sage-700">
                · {c.type === "amount"
                    ? `$${Number(c.amount_off).toFixed(2)} off`
                    : `${c.pct}% off`}
                {" "}(−${Number(c.discount_amount).toFixed(2)})
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => { onRemove(c.code); setSuccess(""); setError(""); }}
            data-testid={`remove-promo-${c.code}`}
            className="text-sm text-charcoal-500 hover:text-charcoal-700 underline"
          >
            Remove
          </button>
        </div>
      ))}

      {/* Welcome-offer toast — one-click apply of FIRST10 for users who
          haven't redeemed it yet. Replaces the old localStorage
          auto-apply so eligibility is checked live against the redemption
          ledger, and repeat customers never see this. */}
      {showWelcome && (
        <div
          data-testid="welcome-offer-toast"
          className="rounded-2xl p-4 bg-plum-50 border border-plum-200 flex items-center justify-between gap-3"
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-plum-700 font-medium mb-0.5">
              Welcome offer
            </p>
            <p className="text-sm text-charcoal-700">
              Apply your <span className="font-medium">{welcome.percent}% off</span> first-order discount ({welcome.code}).
            </p>
          </div>
          <button
            type="button"
            disabled={submitting}
            data-testid="welcome-offer-apply"
            onClick={() => applyCode(welcome.code, /* closeAfter */ false)}
            className="text-sm font-medium px-4 py-2 rounded-full bg-plum-600 text-white hover:bg-plum-700 disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      )}

      {(!hasAny || entryOpen) ? (
        <form
          onSubmit={apply}
          data-testid="promo-code-form"
          className="rounded-2xl p-4 bg-cream-200/60 border border-charcoal-100"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2">
            {hasAny ? "Add another code" : "Have a promo code?"}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="promo-code-input"
              className="input flex-1 uppercase tracking-wider"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="apply-promo-button"
              className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              {submitting ? "Checking…" : "Apply"}
            </button>
          </div>
          {error && (
            <p data-testid="promo-code-error" className="mt-2 text-xs text-red-700">
              {error}
            </p>
          )}
          {success && (
            <p data-testid="promo-code-success" className="mt-2 text-xs text-sage-700">
              {success}
            </p>
          )}
        </form>
      ) : (
        <button
          type="button"
          onClick={() => { setEntryOpen(true); setError(""); setSuccess(""); }}
          data-testid="promo-add-another"
          className="w-full rounded-2xl border border-dashed border-charcoal-200 bg-cream-100 hover:bg-cream-200/60 py-3 text-sm font-medium text-charcoal-500 hover:text-[#8B2252] transition-colors"
        >
          + Add another code
        </button>
      )}
    </div>
  );
};


// ---------- Shipping method preview (Standard-only) ----------
const ShippingMethodSelector = ({
  isAddressComplete, loading, error,
  options, isStandardFree, freeThreshold,
}) => {
  // Show only the standard tier — pulled from /api/shipping/rates if
  // available, otherwise a static fallback.
  const standard = (options || []).find((o) => o.method === "standard") || {
    method: "standard", label: "Standard Shipping",
    price: isStandardFree ? 0 : 14,
    estimated_days: "2-4 business days",
    carriers: [{ id: "usps_priority", label: "USPS Priority Mail", estimated_days: "2-4 business days" }],
  };
  // When the threshold is hit the API returns price 0 — show "Free" rather
  // than the static fallback.
  const displayPrice = isStandardFree ? 0 : standard.price;
  const carrierLabel = standard.carriers?.[0]?.label || "USPS Priority Mail";

  return (
    <section
      data-testid="shipping-method-selector"
      className="surface-card p-6 md:p-7"
    >
      <div className="flex items-center gap-2 mb-1">
        <Truck className="w-4 h-4 text-sage-600" strokeWidth={1.8} />
        <p className="overline">Shipping Method</p>
      </div>
      {!isAddressComplete ? (
        <p
          data-testid="shipping-method-prompt"
          className="text-sm text-charcoal-400 mt-2"
        >
          Fill in your shipping address above to see your shipping option.
        </p>
      ) : (
        <>
          <p className="text-xs text-charcoal-400 mb-5 inline-flex items-center gap-1.5">
            {loading && (
              <Loader2
                data-testid="shipping-rates-spinner"
                className="w-3.5 h-3.5 animate-spin text-sage-600"
                strokeWidth={2}
              />
            )}
            {loading
              ? "Verifying address with our carrier…"
              : `Ships from Boca Raton, FL. Free over $${Number(freeThreshold).toFixed(0)}.`}
          </p>

          {error && (
            <div
              data-testid="shipping-method-error"
              className="mb-4 p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs"
            >
              {error}
            </div>
          )}

          <div
            data-testid="shipping-tier-standard"
            className="text-left rounded-2xl p-4 border-2 border-[#8B2252] bg-[#FDFAF9] shadow-sm"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-[#8B2252] bg-[#8B2252] shrink-0 mt-0.5 relative">
                  <span className="absolute inset-1 bg-white rounded-full" />
                </span>
                <span className="font-display font-bold text-charcoal-600">
                  {standard.label}
                </span>
              </div>
            </div>
            <p className="text-xs text-charcoal-400 mb-2">
              {standard.estimated_days} · {carrierLabel}
            </p>
            <p
              data-testid="shipping-tier-price-standard"
              className="font-display font-bold text-lg text-[#8B2252]"
            >
              {displayPrice === 0 ? "Free" : `$${Number(displayPrice).toFixed(2)}`}
            </p>
          </div>
        </>
      )}
    </section>
  );
};
